import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import cs from "classnames";
import styles from "./styles.module.scss";
import { SearchIcon } from "../AppInsightsControls/icons";
import { CaretDownIcon, CaretUpIcon } from "./icons";
import {
  SearchSelectIconShape,
  SearchSelectOptionProps,
  SearchSelectProps,
  SearchSelectSectionProps,
} from "./types";

const SearchSelectContext = createContext<{
  iconShape: SearchSelectIconShape;
  select: () => void;
}>({ iconShape: "app", select: () => {} });

const OPTION_SELECTOR = '[role="option"]';

const MENU_GAP = 4;
// Keep in sync with .list max-height and the .dropdown padding + border.
const LIST_MAX_HEIGHT = 240;
const DROPDOWN_CHROME = 18;
const VIEWPORT_MARGIN = 8;
const LIST_MIN_HEIGHT = 80;

type Placement = { style: React.CSSProperties; listMaxHeight: number };

/**
 * DS searchable select — "Select app" / "Select country" of the New order and
 * Add app steps (Figma 4147:17976, 4147:17979). Closed it shows the value or
 * the placeholder; opened, the field itself becomes the search input and the
 * list drops below it — portaled to <body> and pinned to the field, so the
 * step's scroll container or the footer can't clip it, and opened upwards
 * when the space below is short. Filtering is the parent's job: it gets the
 * query via `onSearchChange` and renders the matching `SearchSelectOption`s.
 */
export const SearchSelect: React.FC<SearchSelectProps> = ({
  label,
  placeholder,
  searchPlaceholder,
  value,
  iconShape = "app",
  size = "medium",
  search,
  onSearchChange,
  onOpen,
  onClose,
  loading,
  emptyText = "Nothing found",
  isEmpty,
  children,
  className,
}) => {
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState<Placement | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const controlRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  // Give focus back to the closed field after a keyboard close or a pick.
  const refocusRef = useRef(false);
  const listId = useId();

  const openList = () => {
    if (open) return;
    setOpen(true);
    onOpen?.();
  };

  const closeList = useCallback(
    (refocus = false) => {
      refocusRef.current = refocus;
      setOpen(false);
      onClose?.();
    },
    [onClose],
  );

  const select = useCallback(() => {
    onSearchChange("");
    closeList(true);
  }, [onSearchChange, closeList]);

  useEffect(() => {
    if (open) {
      inputRef.current?.focus();
      return;
    }
    if (refocusRef.current) {
      refocusRef.current = false;
      triggerRef.current?.focus();
    }
  }, [open]);

  const placeDropdown = useCallback(() => {
    const box = controlRef.current?.getBoundingClientRect();
    if (!box) return;
    const below = window.innerHeight - box.bottom - MENU_GAP - VIEWPORT_MARGIN;
    const above = box.top - MENU_GAP - VIEWPORT_MARGIN;
    // Decided by the full list height, not the current matches, so the list
    // doesn't flip sides while the query narrows it.
    const openUp = below < LIST_MAX_HEIGHT + DROPDOWN_CHROME && above > below;
    const room = (openUp ? above : below) - DROPDOWN_CHROME;
    setPlacement({
      style: {
        left: box.left,
        width: box.width,
        ...(openUp
          ? { bottom: window.innerHeight - box.top + MENU_GAP }
          : { top: box.bottom + MENU_GAP }),
      },
      listMaxHeight: Math.max(Math.min(LIST_MAX_HEIGHT, room), LIST_MIN_HEIGHT),
    });
  }, []);

  useLayoutEffect(() => {
    if (open) placeDropdown();
    else setPlacement(null);
  }, [open, placeDropdown]);

  useEffect(() => {
    if (!open) return;
    const handleOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        rootRef.current?.contains(target) ||
        dropdownRef.current?.contains(target)
      )
        return;
      closeList();
    };
    // A scroll anywhere moves the field — follow it. The list's own scroll
    // doesn't (and the capture listener sees it too), so skip it.
    const handleScroll = (event: Event) => {
      if (dropdownRef.current?.contains(event.target as Node)) return;
      placeDropdown();
    };
    document.addEventListener("mousedown", handleOutside);
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", placeDropdown);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", placeDropdown);
    };
  }, [open, closeList, placeDropdown]);

  const options = () =>
    Array.from(
      listRef.current?.querySelectorAll<HTMLElement>(OPTION_SELECTOR) ?? [],
    );

  const handleInputKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      options()[0]?.focus();
    } else if (event.key === "Escape") {
      closeList(true);
    } else if (event.key === "Tab") {
      closeList();
    }
  };

  const handleListKeyDown = (event: React.KeyboardEvent) => {
    const items = options();
    const index = items.indexOf(document.activeElement as HTMLElement);
    if (event.key === "ArrowDown") {
      event.preventDefault();
      items[Math.min(index + 1, items.length - 1)]?.focus();
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      if (index <= 0) inputRef.current?.focus();
      else items[index - 1]?.focus();
    } else if (event.key === "Escape") {
      closeList(true);
    } else if (event.key === "Tab") {
      closeList();
    }
  };

  const shapeClass = iconShape === "flag" ? styles.iconFlag : styles.iconApp;

  return (
    <div
      ref={rootRef}
      className={cs(
        styles.root,
        { [styles.small]: size === "small" },
        className,
      )}
    >
      {label && <span className={styles.label}>{label}</span>}
      <div ref={controlRef} className={styles.control}>
        {open ? (
          <div
            className={cs(styles.field, styles.fieldOpen)}
            onMouseDown={(event) => {
              // Clicks on the padding keep the caret in the input.
              if (event.target !== inputRef.current) event.preventDefault();
              inputRef.current?.focus();
            }}
          >
            <SearchIcon className={styles.searchIcon} />
            <input
              ref={inputRef}
              className={styles.input}
              value={search}
              placeholder={searchPlaceholder}
              onChange={(event) => onSearchChange(event.target.value)}
              onKeyDown={handleInputKeyDown}
              role="combobox"
              aria-expanded
              aria-controls={listId}
              aria-autocomplete="list"
              aria-label={label}
              autoComplete="off"
              spellCheck={false}
            />
            <button
              type="button"
              className={styles.caretButton}
              onClick={() => closeList(true)}
              aria-label="Close list"
            >
              <CaretUpIcon className={styles.caret} />
            </button>
          </div>
        ) : (
          <button
            ref={triggerRef}
            type="button"
            className={styles.field}
            onClick={openList}
            onKeyDown={(event) => {
              if (event.key === "ArrowDown") {
                event.preventDefault();
                openList();
              }
            }}
            aria-haspopup="listbox"
            aria-expanded={false}
          >
            {value?.icon && (
              <img
                className={cs(styles.valueIcon, shapeClass)}
                src={value.icon}
                alt=""
                referrerPolicy="no-referrer"
              />
            )}
            <span
              className={cs(styles.value, {
                [styles.placeholder]: !value?.label,
              })}
            >
              {value?.label || placeholder}
            </span>
            <CaretDownIcon className={styles.caret} />
          </button>
        )}

        {open &&
          placement &&
          createPortal(
            <div
              ref={dropdownRef}
              className={styles.dropdown}
              style={placement.style}
            >
              <div
                ref={listRef}
                id={listId}
                className={styles.list}
                style={{ maxHeight: placement.listMaxHeight }}
                role="listbox"
                aria-label={label}
                onKeyDown={handleListKeyDown}
              >
                {loading ? (
                  <div className={styles.status}>Loading…</div>
                ) : isEmpty ? (
                  <div className={styles.status}>{emptyText}</div>
                ) : (
                  <SearchSelectContext.Provider value={{ iconShape, select }}>
                    {children}
                  </SearchSelectContext.Provider>
                )}
              </div>
            </div>,
            document.body,
          )}
      </div>
    </div>
  );
};

/** One row of the list (Figma "ListItem/App - M", 101:2401). */
export const SearchSelectOption: React.FC<SearchSelectOptionProps> = ({
  label,
  icon,
  iconShape,
  selected,
  trailing,
  title,
  error,
  onSelect,
}) => {
  const context = useContext(SearchSelectContext);
  const shape = iconShape ?? context.iconShape;
  const errorId = useId();

  const option = (
    <button
      type="button"
      role="option"
      aria-selected={Boolean(selected)}
      aria-invalid={error ? true : undefined}
      aria-describedby={error ? errorId : undefined}
      className={cs(styles.option, {
        [styles.optionSelected]: selected,
        [styles.optionError]: error,
      })}
      title={title}
      onClick={async () => {
        if ((await onSelect()) === false) return;
        context.select();
      }}
    >
      {icon ? (
        <img
          className={cs(
            styles.optionIcon,
            shape === "flag" ? styles.iconFlag : styles.iconApp,
          )}
          src={icon}
          alt=""
          loading="lazy"
          referrerPolicy="no-referrer"
        />
      ) : (
        <span className={cs(styles.optionIcon, styles.iconBlank)} />
      )}
      <span className={styles.optionLabel}>{label}</span>
      {trailing && <span className={styles.optionTrailing}>{trailing}</span>}
    </button>
  );

  if (!error) return option;
  return (
    <>
      {option}
      <span id={errorId} className={styles.optionErrorText} role="alert">
        {error}
      </span>
    </>
  );
};

/** Captioned group of rows, e.g. "Favorite countries" or a region. */
export const SearchSelectSection: React.FC<SearchSelectSectionProps> = ({
  title,
  children,
}) => (
  <div className={styles.section} role="group" aria-label={title}>
    <span className={styles.sectionTitle}>{title}</span>
    {children}
  </div>
);

export default SearchSelect;
